import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { retryTransient } from '../utils/http-retry.utils';
import {
  CreateFeatureFlagRequest,
  FeatureFlag,
  UpdateFeatureFlagRequest,
} from '../models/feature-flag.models';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/featureflags`;
  private featureFlagCache = new Map<string, BehaviorSubject<boolean>>();

  // Get all feature flags
  getAllFeatureFlags(): Observable<FeatureFlag[]> {
    return this.http
      .get<FeatureFlag[]>(this.apiUrl)
      .pipe(retryTransient(1));
  }

  // Get active feature flags
  getActiveFeatureFlags(): Observable<FeatureFlag[]> {
    return this.http
      .get<FeatureFlag[]>(`${this.apiUrl}/active`)
      .pipe(retryTransient(1));
  }

  // Get feature flag by ID
  getFeatureFlagById(id: number): Observable<FeatureFlag> {
    return this.http
      .get<FeatureFlag>(`${this.apiUrl}/${id}`)
      .pipe(retryTransient(1));
  }

  // Get feature flag by key
  getFeatureFlagByKey(key: string): Observable<FeatureFlag> {
    return this.http
      .get<FeatureFlag>(`${this.apiUrl}/key/${key}`)
      .pipe(retryTransient(1));
  }

  // Check if feature flag is enabled (reactive)
  isFeatureFlagEnabled(key: string): Observable<boolean> {
    // If not cached, create new subject and fetch initial value
    if (!this.featureFlagCache.has(key)) {
      this.featureFlagCache.set(key, new BehaviorSubject<boolean>(false));
      this.fetchAndUpdateFlag(key);
    }

    return this.featureFlagCache.get(key)!.asObservable();
  }

  // Load multiple feature flags at once (bulk loading for better performance)
  loadFeatureFlags(keys: string[]): Observable<Map<string, boolean>> {
    return this.http
      .post<{ [key: string]: boolean }>(`${this.apiUrl}/bulk-check`, keys)
      .pipe(
        map((response) => {
          const flagMap = new Map<string, boolean>();

          // Update individual caches and build result map
          keys.forEach((key) => {
            const value = response[key] || false;
            flagMap.set(key, value);

            // Update or create cache entry
            if (!this.featureFlagCache.has(key)) {
              this.featureFlagCache.set(
                key,
                new BehaviorSubject<boolean>(value)
              );
            } else {
              this.featureFlagCache.get(key)!.next(value);
            }
          });

          return flagMap;
        }),
      );
  }

  // Fetch and update flag status
  private fetchAndUpdateFlag(key: string): void {
    const url = `${this.apiUrl}/enabled/${key}`;

    this.http
      .get<boolean>(url)
      .pipe(retryTransient(1))
      .subscribe({
        next: (isEnabled) => {
          if (this.featureFlagCache.has(key)) {
            this.featureFlagCache.get(key)!.next(isEnabled);
          }
        },
        error: (error) => {
          console.error('Error fetching feature flag:', error);
          // Keep current value or default to false
        },
      });
  }

  // Create feature flag
  createFeatureFlag(
    request: CreateFeatureFlagRequest
  ): Observable<FeatureFlag> {
    return this.http.post<FeatureFlag>(this.apiUrl, request);
  }

  // Update feature flag
  updateFeatureFlag(
    id: number,
    request: UpdateFeatureFlagRequest
  ): Observable<FeatureFlag> {
    return this.http.put<FeatureFlag>(`${this.apiUrl}/${id}`, request);
  }

  // Toggle feature flag
  toggleFeatureFlag(id: number): Observable<boolean> {
    // First get the flag to know its key and current state
    return this.getFeatureFlagById(id).pipe(
      switchMap((flag) => {
        if (!flag) {
          return throwError(() => new Error('Feature flag not found'));
        }

        const newState = !flag.isEnabled;

        return this.http.patch<boolean>(`${this.apiUrl}/${id}/toggle`, {}).pipe(
          tap((success) => {
            if (success) {
              // Update cache directly with new state instead of re-fetching
              this.updateCacheDirectly(flag.key, newState);
            }
          })
        );
      }),
    );
  }

  // Update cache directly with new state (no API call needed)
  private updateCacheDirectly(flagKey: string, newState: boolean): void {
    if (this.featureFlagCache.has(flagKey)) {
      this.featureFlagCache.get(flagKey)!.next(newState);
    }
  }

  // Refresh a specific feature flag by key (fallback method)
  refreshSpecificFeatureFlag(flagKey: string): void {
    if (this.featureFlagCache.has(flagKey)) {
      this.fetchAndUpdateFlag(flagKey);
    }
  }

  // Refresh all cached feature flags
  refreshAllFeatureFlags(): void {
    this.featureFlagCache.forEach((subject, cacheKey) => {
      this.fetchAndUpdateFlag(cacheKey);
    });
  }

  // Archive feature flag
  archiveFeatureFlag(id: number): Observable<boolean> {
    return this.http.patch<boolean>(`${this.apiUrl}/${id}/archive`, {}).pipe(
      tap(() => {
        // Refresh all cached feature flags after archive
        this.refreshAllFeatureFlags();
      }),
    );
  }

  // Delete feature flag
  deleteFeatureFlag(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        // Refresh all cached feature flags after delete
        this.refreshAllFeatureFlags();
      }),
    );
  }

  // Check if feature flag exists
  featureFlagExists(key: string): Observable<boolean> {
    return this.http
      .get<boolean>(`${this.apiUrl}/exists/${key}`)
      .pipe(retryTransient(1));
  }

  // Helper method to format dates
  formatDate(dateString: string | null): string {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Helper method to format dates in local time
  formatRelativeDate(dateString: string | null): string {
    if (!dateString) return 'Never';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    // Format as local date: MM/DD/YYYY
    return new Intl.DateTimeFormat('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }
}
